import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const DEFAULT_WIDTH = 650;
const MIN_WIDTH = 360;

/**
 * The room a side panel must leave for whatever sits beside it — enough for a form to render one
 * readable control per row plus its page padding. A related-view panel defaults to "most of the
 * window" and a width dragged on a bigger screen outlives that screen, so without a floor the form
 * beside it collapses (measured: a 16px form grid, and DataGrids reporting zero width).
 */
export const PANEL_SIBLING_MIN_WIDTH = 420;

/**
 * Cap for the panel element itself. Percentages resolve against the flex row the panel shares with
 * the content, so this tracks the real split — unlike window.innerWidth, which also counts the
 * left navigation the panel never gets to take. Apply to the drawer root; give its paper
 * `maxWidth: '100%'` so it follows.
 */
export const panelMaxWidthSx = { maxWidth: `calc(100% - ${PANEL_SIBLING_MIN_WIDTH}px)` } as const;

/**
 * Same floor for the persisted number, so a stored width cannot keep growing past what is usable.
 * Measured against the viewport because the drag handler only knows viewport coordinates; the CSS
 * cap above is what actually holds the line.
 */
const VIEWPORT_MARGIN = PANEL_SIBLING_MIN_WIDTH;

/**
 * The current route path with dynamic id segments (UUIDs / pure numbers) stripped — a stable
 * identity for the *form*, not the record. So every Plan Header edit form shares one saved layout,
 * regardless of which header is open. Used to scope both the panel width and its grid state.
 */
export function formRouteKey(): string {
  return (typeof window !== 'undefined' ? window.location.pathname : '')
    .split('/')
    .filter(seg => seg && !/^[0-9]+$/.test(seg) && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg))
    .join('.');
}

function widthStorageKey(title: string): string {
  return `nexo-panel-width.${formRouteKey()}.${title}`;
}

export interface ResizablePanelOptions {
  /** Initial width when no saved width exists. */
  defaultWidth?: number;
  /**
   * 'form' (default) remembers the width per form route. 'global' shares one
   * saved width app-wide — for panels that live outside any form, like the
   * AI assistant in the always-mounted Layout.
   */
  storageScope?: 'form' | 'global';
  /**
   * Which edge the panel is anchored to. 'right' (default) is a slide-in panel that grows
   * leftward, with the grab strip on its left edge. 'left' is a panel fixed to the left
   * (e.g. a tree in a split view) that grows rightward, with the grab strip on its right edge.
   */
  anchor?: 'left' | 'right';
  /** Smallest width the panel may be dragged to. Defaults to 360 (sized for slide-in drawers). */
  minWidth?: number;
}

/**
 * Width state + a drag-to-resize handle for a side panel. The chosen width is persisted in
 * localStorage (per form or globally, see {@link ResizablePanelOptions}) so it survives reloads.
 *
 * Returns the live `width` and a `<ResizeHandle/>` to render inside the (position: relative) panel;
 * the handle sits on whichever edge faces the resize direction (see {@link ResizablePanelOptions.anchor}).
 */
export function useResizablePanelWidth(title: string, options: ResizablePanelOptions = {}) {
  const {
    defaultWidth = DEFAULT_WIDTH,
    storageScope = 'form',
    anchor = 'right',
    minWidth = MIN_WIDTH,
  } = options;
  const storageKey = storageScope === 'global'
    ? `nexo-panel-width.global.${title}`
    : widthStorageKey(title);

  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      const n = saved ? parseInt(saved, 10) : NaN;
      if (!Number.isFinite(n)) return defaultWidth;
      // A width saved on a larger screen must not exceed this viewport.
      const max = Math.max(minWidth, window.innerWidth - VIEWPORT_MARGIN);
      return Math.min(max, Math.max(minWidth, n));
    } catch {
      return defaultWidth;
    }
  });

  const dragging = useRef(false);
  // Mirror of `width` for the drag start capture, so onMouseDown needn't re-bind on every resize.
  const widthRef = useRef(width);
  widthRef.current = width;
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = widthRef.current;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      // 'left'-anchored panels grow as the cursor moves right (delta from drag start);
      // 'right'-anchored slide-ins grow as the cursor moves left toward the viewport edge.
      const next = anchor === 'left'
        ? dragStartWidth.current + (e.clientX - dragStartX.current)
        : window.innerWidth - e.clientX;
      const max = Math.max(minWidth, window.innerWidth - VIEWPORT_MARGIN);
      setWidth(Math.min(max, Math.max(minWidth, next)));
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      setWidth(w => {
        try { localStorage.setItem(storageKey, String(Math.round(w))); } catch { /* ignore */ }
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [storageKey, anchor, minWidth]);

  /** A thin vertical grab strip on the panel's resize edge. */
  const ResizeHandle = useCallback(() => (
    <Box
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      sx={{
        position: 'absolute',
        ...(anchor === 'left' ? { right: 0 } : { left: 0 }),
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        zIndex: theme => theme.zIndex.drawer + 1,
        '&:hover': { backgroundColor: 'primary.main', opacity: 0.4 },
        '&:active': { backgroundColor: 'primary.main', opacity: 0.6 },
      }}
    />
  ), [onMouseDown, anchor]);

  return { width, ResizeHandle };
}
